import { Fragment } from "react";
import { parseMarkdown, renderInline, type MarkdownBlock } from "../../lib/markdown";
import { ArrowUpRight, PillButton, SectionBadge } from "./SitePrimitives";

export type BlogCardPost = {
  href: string;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  image?: string;
};

function PostMeta({ author, date, className = "" }: { author: string; date: string; className?: string }) {
  return (
    <p className={`site-body flex items-center gap-3 text-[#A3A3A3] min-[1200px]:text-[15px] ${className}`}>
      {author ? <span className="text-white">{author}</span> : null}
      {author && date ? <span className="h-[5px] w-[5px] rounded-full bg-[#A3A3A3]" /> : null}
      {date ? <span>{date}</span> : null}
    </p>
  );
}

/** Grid card on desktop and tablet, image-left row on phones (Framer's blog list). */
export function BlogCard({ post }: { post: BlogCardPost }) {
  return (
    <a href={post.href} className="group flex gap-6 min-[810px]:block">
      {post.image ? (
        <img
          src={post.image}
          alt=""
          loading="lazy"
          className="h-40 w-[153px] shrink-0 rounded-2xl object-cover min-[810px]:h-[238px] min-[810px]:w-full"
        />
      ) : null}
      <div className="relative flex min-w-0 flex-col gap-[10px] min-[810px]:gap-3 min-[810px]:px-3 min-[810px]:pt-[18px]">
        <span className="absolute right-3 top-[18px] hidden text-[#627072] transition-colors group-hover:text-white min-[810px]:block">
          <ArrowUpRight className="h-[10px] w-[10px]" />
        </span>
        {post.category ? (
          <p className="font-['Inter'] text-[12px] font-semibold uppercase leading-[12px] text-[#1D9793] min-[810px]:text-[14px] min-[810px]:leading-[20px]">
            {post.category}
          </p>
        ) : null}
        <h3 className="line-clamp-2 font-['Inter'] text-[16px] font-bold leading-[1.3] tracking-[-0.01em] text-white min-[810px]:line-clamp-none min-[810px]:pr-8 min-[810px]:text-[24px] min-[810px]:font-extrabold min-[810px]:leading-[1.2]">
          {post.title}
        </h3>
        {post.excerpt ? (
          <p className="site-body line-clamp-2 text-[#9F9F9F] min-[810px]:line-clamp-3 min-[810px]:text-[16px] min-[810px]:leading-[24px]">
            {post.excerpt}
          </p>
        ) : null}
        <PostMeta author="" date={post.date} className="min-[810px]:hidden" />
        <PostMeta author={post.author} date={post.date} className="hidden min-[810px]:flex" />
      </div>
    </a>
  );
}

function BlogGrid({ posts }: { posts: BlogCardPost[] }) {
  return (
    <div className="grid gap-8 min-[810px]:grid-cols-2 min-[810px]:gap-x-8 min-[810px]:gap-y-8 min-[1200px]:grid-cols-3">
      {posts.map((post) => (
        <BlogCard key={post.href} post={post} />
      ))}
    </div>
  );
}

export function BlogIndexPage({
  badge,
  title,
  description,
  image,
  posts,
  promo,
  onCta,
}: {
  badge: string;
  title: string;
  description: string;
  image?: string;
  posts: BlogCardPost[];
  promo: { title: string; text: string; cta: string };
  onCta: (href: string, placement: string) => void;
}) {
  const [featured, ...rest] = posts;
  return (
    <>
      <section id="hero" className="relative h-[470px] overflow-hidden min-[810px]:h-[435px]">
        {image ? <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.7)_0%,var(--public-ink)_100%)]" />
        <div className="relative mx-auto flex max-w-[864px] flex-col items-center px-6 pt-[183px] text-center min-[810px]:pt-[210px]">
          <SectionBadge>{badge}</SectionBadge>
          <h1 className="site-h2 mt-4 text-white">{title}</h1>
          {description ? <p className="site-lead mt-4 text-white/80">{description}</p> : null}
        </div>
      </section>

      <section className="site-container pt-10 min-[810px]:pt-[120px]">
        {featured ? (
          <div className="grid gap-[52px] min-[1200px]:grid-cols-[632px_1fr] min-[1200px]:gap-12">
            <a href={featured.href} className="group block">
              {featured.image ? (
                <img
                  src={featured.image}
                  alt=""
                  className="h-[214px] w-full rounded-2xl object-cover min-[810px]:h-[271px]"
                />
              ) : null}
              <div className="relative px-3 pt-[18px]">
                <span className="absolute right-3 top-[18px] text-[#627072] transition-colors group-hover:text-white">
                  <ArrowUpRight className="h-[10px] w-[10px]" />
                </span>
                {featured.category ? (
                  <p className="font-['Inter'] text-[14px] font-semibold uppercase leading-[20px] text-[#1D9793]">
                    {featured.category}
                  </p>
                ) : null}
                <h2 className="site-h2 mt-3 max-w-[594px] pr-6 text-white">{featured.title}</h2>
                {featured.excerpt ? <p className="site-lead mt-3">{featured.excerpt}</p> : null}
                <PostMeta author={featured.author} date={featured.date} className="mt-3" />
              </div>
            </a>
            <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-2xl bg-[#0E2827] px-6 py-[60px] text-center min-[810px]:rounded-3xl min-[1200px]:px-8">
              <img
                src="/site/cta-watermark.svg"
                alt=""
                className="pointer-events-none absolute left-[-40px] top-[160px] h-[470px] w-[1723px] max-w-none opacity-60 min-[1200px]:top-[180px]"
              />
              <div className="relative flex max-w-[448px] flex-col items-center">
                <h2 className="site-h2 text-white">{promo.title}</h2>
                {promo.text ? <p className="site-lead mt-4 text-white/80">{promo.text}</p> : null}
                {promo.cta ? (
                  <PillButton href="#contact" className="mt-8" onClick={() => onCta("#contact", "blog-promo")}>
                    {promo.cta}
                  </PillButton>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        {rest.length ? (
          <div className="mt-[72px] min-[810px]:mt-[110px]">
            <BlogGrid posts={rest} />
          </div>
        ) : null}
      </section>
    </>
  );
}

function MarkdownBody({ blocks, onCta }: { blocks: MarkdownBlock[]; onCta: () => void }) {
  return (
    <div className="site-post-body">
      {blocks.map((block, index) => {
        const key = `b-${index}`;
        switch (block.type) {
          case "heading":
            return block.level === 2 ? (
              <h2 key={key}>{renderInline(block.text, key)}</h2>
            ) : (
              <h3 key={key}>{renderInline(block.text, key)}</h3>
            );
          case "list": {
            const Tag = block.ordered ? "ol" : "ul";
            return (
              <Tag key={key}>
                {block.items.map((item, i) => (
                  <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
                ))}
              </Tag>
            );
          }
          case "quote":
            return <blockquote key={key}>{renderInline(block.text, key)}</blockquote>;
          case "image":
            return <img key={key} src={block.src} alt={block.alt} loading="lazy" />;
          case "table":
            return (
              <div key={key} className="site-post-table">
                <table>
                  <thead>
                    <tr>
                      {block.head.map((cell, i) => (
                        <th key={i}>{renderInline(cell, `${key}-h${i}`)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, i) => (
                          <td key={i}>{renderInline(cell, `${key}-${r}-${i}`)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "cta":
            return (
              <div
                key={key}
                className="!mt-12 flex flex-col items-center gap-6 rounded-xl bg-[#0F2827] px-6 py-8 text-center min-[810px]:flex-row min-[810px]:justify-center min-[810px]:gap-8 min-[810px]:px-16"
              >
                <h4 className="max-w-[307px] font-['Red_Hat_Display'] text-[20px] font-extrabold leading-[1.2] text-white min-[810px]:text-[24px]">
                  {block.title}
                </h4>
                {block.label ? (
                  <PillButton href="#contact" onClick={onCta}>
                    {block.label}
                  </PillButton>
                ) : null}
              </div>
            );
          default:
            return <p key={key}>{renderInline(block.text, key)}</p>;
        }
      })}
    </div>
  );
}

export function BlogPostPage({
  post,
  body,
  related,
  labels,
  homeHref,
  blogHref,
  onCta,
}: {
  post: BlogCardPost;
  body: string;
  related: BlogCardPost[];
  labels: { home: string; blog: string; related: string; viewAll: string };
  homeHref: string;
  blogHref: string;
  onCta: (href: string, placement: string) => void;
}) {
  const blocks = parseMarkdown(body);
  return (
    <>
      <article id="hero" className="pt-[82px] min-[810px]:pt-[100px]">
        <nav
          aria-label="Breadcrumb"
          className="mx-auto flex max-w-[1200px] items-center justify-center gap-2 px-6 text-[13px] font-medium leading-[1.5] text-[#A3A3A3] min-[810px]:text-[15px]"
        >
          {[
            { label: labels.home, href: homeHref },
            { label: labels.blog, href: blogHref },
          ].map((crumb) => (
            <Fragment key={crumb.href}>
              <a href={crumb.href} className={crumb.href === homeHref ? "text-white" : "hover:text-white"}>
                {crumb.label}
              </a>
              <span>&gt;</span>
            </Fragment>
          ))}
          <span className="max-w-[193px] truncate">{post.title}</span>
        </nav>
        <header className="mx-auto mt-10 flex max-w-[908px] flex-col items-center px-5 text-center min-[810px]:mt-[86px] min-[810px]:px-6">
          {post.category ? (
            <p className="font-['Inter'] text-[14px] font-bold uppercase leading-[1.2] text-[#25E1D8]">{post.category}</p>
          ) : null}
          <h1 className="site-h2 mt-5 max-w-[620px] text-white">{post.title}</h1>
          {post.excerpt ? <p className="site-lead mt-5 max-w-[280px] text-white/80 min-[810px]:max-w-[480px]">{post.excerpt}</p> : null}
          <PostMeta author={post.author} date={post.date} className="mt-5" />
        </header>
        {post.image ? (
          <div className="site-container mt-10 min-[810px]:mt-14">
            <img
              src={post.image}
              alt=""
              className="h-[240px] w-full rounded-2xl object-cover min-[810px]:h-[420px] min-[1200px]:h-[560px]"
            />
          </div>
        ) : null}
        <div className="mx-auto mt-6 max-w-[728px] px-6 min-[810px]:mt-[72px]">
          <MarkdownBody blocks={blocks} onCta={() => onCta("#contact", "blog-post")} />
        </div>
      </article>

      {related.length ? (
        <section className="site-container pt-[120px] min-[810px]:pt-[200px]">
          <div className="flex flex-col gap-6 min-[810px]:flex-row min-[810px]:items-end min-[810px]:justify-between">
            <h2 className="site-h2 text-white">{labels.related}</h2>
            <a
              href={blogHref}
              className="inline-flex items-center gap-2 pb-[6px] text-[16px] font-bold leading-[1.45] text-[#9F9F9F] transition-colors hover:text-white"
            >
              {labels.viewAll}
              <ArrowUpRight />
            </a>
          </div>
          <div className="mt-8 min-[810px]:mt-[60px]">
            <BlogGrid posts={related} />
          </div>
        </section>
      ) : null}
    </>
  );
}
